import React, { FC, useEffect, useMemo } from 'react';
import { createSignal } from '@funtech-int/rival';
import { useSignal } from '@funtech-int/rival-react';
import classnames from 'classnames';
import { createPictureFactory, useFeatureFlags } from '@funtech-int/yango-shared';

import { BackgroundPlayer as LegacyBackgroundPlayer } from '@/scenarios/legacy-player/components/BackgroundPlayer/BackgroundPlayer';
import {
    TelemetryContentPlace,
    TelemetryContentType,
} from '@/scenarios/legacy-player/components/Player';
import { PlayerControlButtonSize } from '@/scenarios/legacy-player/components/PlayerControls/parts/PlayerControlButton.types';
import { useWidget } from '@/shared/hooks/useWidget';
import { backgroundPlayerWidget } from '@/scenarios/player';

import styles from './BackgroundTrailer.module.css';

interface BackgroundTrailerProps {
    title: string;
    contentId?: string;
    backgroundUrl?: string;
    buttonSize?: 'xl' | 'xxl';
    onTrailerStart?: VoidFunction;
    onTrailerEnd?: VoidFunction;
}

type BackgroundTrailerState = 'idle' | 'background' | 'background_error' | 'trailer';

const pictureFactory = createPictureFactory({
    fallback: {
        src: '2016x1134',
    },
});

export const BackgroundTrailer: FC<BackgroundTrailerProps> = ({
    title,
    contentId,
    backgroundUrl,
    buttonSize = 'xl',
    onTrailerStart,
    onTrailerEnd,
}) => {
    const { img } = pictureFactory({
        fallbackUrl: backgroundUrl,
    });

    const BackgroundPlayer = useWidget(backgroundPlayerWidget);
    const { flags } = useFeatureFlags();
    const isNewBackgroundPlayerEnabled = flags.get(
        'isNewBackgroundPlayerEnabled',
        'boolean',
        false
    );

    const controller = useMemo(() => {
        const signal = createSignal<BackgroundTrailerState>('idle');
        let timer: number;

        return {
            signal: signal.seal(),

            init() {
                timer = window.setTimeout(() => {
                    signal.set('background');
                }, 3000);

                return () => clearTimeout(timer);
            },

            onBackgroundLoad() {
                if (signal.get() === 'idle') {
                    clearTimeout(timer);
                    signal.set('background');
                }
            },

            onBackgroundError() {
                if (signal.get() === 'idle') {
                    clearTimeout(timer);
                    signal.set('background_error');
                }
            },

            onTrailerStart() {
                onTrailerStart?.();
                signal.set('trailer');
            },

            onTrailerEnd() {
                onTrailerEnd?.();
                signal.set('idle');
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentId, onTrailerStart, onTrailerEnd]);

    const state = useSignal(controller.signal);
    useEffect(() => controller.init(), [controller]);

    return (
        <div className={styles.root}>
            {img.src && state !== 'background_error' ? (
                <img
                    key={img.src}
                    className={classnames(styles.background, {
                        [styles.visible]: state !== 'trailer',
                    })}
                    src={img.src}
                    alt={title}
                    onLoad={controller.onBackgroundLoad}
                    onError={controller.onBackgroundError}
                />
            ) : null}
            {contentId && state !== 'idle' ? (
                <div
                    className={classnames(styles.trailer, {
                        [styles.visible]: state === 'trailer',
                    })}
                >
                    {isNewBackgroundPlayerEnabled ? (
                        <BackgroundPlayer
                            contentId={contentId}
                            buttonSize={buttonSize === 'xxl' ? 'size60' : 'size52'}
                            onStart={controller.onTrailerStart}
                            onFinish={controller.onTrailerEnd}
                        />
                    ) : (
                        <LegacyBackgroundPlayer
                            contentId={contentId}
                            buttonSize={buttonSize as PlayerControlButtonSize}
                            telemetryContentPlace={TelemetryContentPlace.MovieScreen}
                            telemetryContentType={TelemetryContentType.Trailer}
                            onStart={controller.onTrailerStart}
                            onFinish={controller.onTrailerEnd}
                        />
                    )}
                </div>
            ) : null}
        </div>
    );
};
